import { useState } from 'react';
import Select from 'react-select';
import {
  Button,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { updateKycDetails } from 'api/kyc';
import { EMAIL_TYPE_OPTIONS } from 'modules/kyc/constants/constants';
import { showError, showToast } from 'utils/notifications';
import KycRfiQuestion from 'components/KycRfi/KycRfiQuestion';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';

const formInitialState = {
  prompt: '',
  is_document_required: false,
  is_front_back_required: false,
  transaction_id: '',
  transaction_ids: [],
  document_type: '',
  document_sub_type: [],
  parent_rfi_id: '',
  send_kyc_link: false,
  document_sub_type_other: '',
  rfi_tag_type: '',
  rfi_tag_subtype: '',
  rfi_tag_type_other: '',
};

const KycRfiForm = ({
  entityId,
  fetchKycDetails,
  kyc_status,
  documentInfo,
  documentInfoError,
  documentInfoLoading,
  parentRfiId,
  rfiTemplates,
  resourceType,
  rfiTagTypes,
  rfiTagSubtypes,
}) => {
  const [questions, setQuestions] = useState([formInitialState]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [isKycStatusUpdate, setIsKycStatusUpdate] = useState(true);
  const [sendRfiEmailToBinance, setSendRfiEmailToBinance] = useState(false);
  const [emailType, setEmailType] = useState('');
  const [pendingEmailType, setPendingEmailType] = useState('');
  const { isOpen: isEmailTypeModalOpen, onOpen: openEmailTypeModal, onClose: closeEmailTypeModal } = useDisclosure();

  const toast = useToast();

  const handleAddNewQuestion = () => {
    setQuestions((prev) => [...prev, formInitialState]);
  };

  const updateQuestion = (index, key, value) => {
    setQuestions((prev) => prev.map((question, i) => (i === index ? { ...question, [key]: value } : question)));
  };

  const updateMultipleQuestions = (index, questions) => {
    setQuestions((prev) => prev.map((question, i) => (i === index ? { ...question, ...questions } : question)));
  };

  const handleDeleteQuestion = (index) => {
    if (questions.length === 1) return;
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTemplateChange = (selectedOption) => {
    setSelectedTemplate(selectedOption);

    const templateQuestions = selectedOption?.value?.map((question) => ({
      ...question,
      rfi_tag_type: question?.rfi_type?.type || '',
      rfi_tag_subtype: question?.rfi_type?.sub_type || '',
    }));

    setQuestions(templateQuestions ? templateQuestions : [formInitialState]);
  };

  const handleSendRfiEmailChange = () => {
    if (!sendRfiEmailToBinance) {
      setPendingEmailType('');
      openEmailTypeModal();
    } else {
      setSendRfiEmailToBinance(false);
      setEmailType('');
    }
  };

  const handleEmailTypeConfirm = () => {
    setSendRfiEmailToBinance(true);
    setEmailType(pendingEmailType);
    closeEmailTypeModal();
  };

  const handleEmailTypeCancel = () => {
    setPendingEmailType('');
    closeEmailTypeModal();
  };

  const handleSubmit = async () => {
    const hasEmptyPrompt = questions.some((question) => !question.prompt.trim());
    const hasInvalidDocument = questions.some(
      (question) =>
        question.is_document_required && (!question.document_type || question.document_sub_type.length === 0)
    );
    const hasInvalidSubType = questions.some(
      (question) => question.is_document_required && question.document_sub_type.includes('')
    );

    if (hasEmptyPrompt) {
      showError(toast, 'Question prompt cannot be empty', '', 'Please fill all the questions');

      return;
    }

    if (hasInvalidDocument || hasInvalidSubType) {
      showError(
        toast,
        'Invalid document details',
        '',
        'If a document is required, please provide a valid type and subtype.'
      );

      return;
    }

    const processedQuestions = questions.map((question) => {
      const existingTransactionIds = Array.isArray(question.transaction_ids) ? question.transaction_ids : [];
      const transactionIdToAdd = question.transaction_id ? question.transaction_id.trim() : '';
      let finalTransactionIds = [...existingTransactionIds];

      if (transactionIdToAdd && !finalTransactionIds.includes(transactionIdToAdd)) {
        finalTransactionIds.push(transactionIdToAdd);
      }

      const updatedQuestion = {
        ...question,
        transaction_ids: finalTransactionIds,
        transaction_id: question.transaction_id || '',
      };

      if (!question.document_sub_type_other && !question.rfi_tag_type) {
        return updatedQuestion;
      }

      const { document_sub_type_other, rfi_tag_type, rfi_tag_subtype, rfi_tag_type_other } = updatedQuestion;

      if (question.document_sub_type.includes('other')) {
        const documentSubtype = [
          ...question.document_sub_type.filter((subtype) => subtype !== 'other'),
          document_sub_type_other,
        ];

        updatedQuestion.document_sub_type = documentSubtype;
      }

      if (question.rfi_tag_type) {
        updatedQuestion.rfi_type = {
          type: rfi_tag_type === 'others' ? rfi_tag_type_other : rfi_tag_type,
          sub_type: rfi_tag_subtype,
        };
      }

      return updatedQuestion;
    });

    setLoading(true);
    const finalQuestions = processedQuestions.length > 0 ? processedQuestions : questions;
    const payload = {
      create_rfi_details: parentRfiId
        ? finalQuestions.map((question) => ({
            ...question,
            parent_rfi_id: parentRfiId,
          }))
        : finalQuestions,
      is_update_kyc_status: isKycStatusUpdate,
      send_rfi_email_to_binance: sendRfiEmailToBinance,
      ...(sendRfiEmailToBinance && emailType ? { email_type: emailType } : {}),
    };

    console.log(payload);

    try {
      await updateKycDetails(entityId, payload);
      setQuestions([formInitialState]);
      showToast(toast, 'RFI Request submitted successfully', 'RFI Request has been submitted successfully.', 'success');
      fetchKycDetails();
    } catch (error) {
      showError(toast, 'Error during submission', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack
      gap={4}
      pt={8}
      px={8}
      pb={6}
      width={'100%'}
      border={'1px solid'}
      borderRadius={'md'}
      borderColor={'gray.400'}
      mt={8}
      maxHeight={'600px'}
    >
      <LoadingErrorWrapper isLoading={documentInfoLoading} error={documentInfoError}>
        <Select
          placeholder='Select RFI Template'
          value={selectedTemplate}
          onChange={handleTemplateChange}
          options={rfiTemplates}
          styles={{
            container: (base) => ({ ...base, backgroundColor: 'white', width: '100%' }),
          }}
          isClearable
        />
        <VStack width={'full'} overflow={'auto'} maxHeight={'475px'}>
          {questions?.map((question, index) => (
            <KycRfiQuestion
              key={index}
              question={question}
              updateQuestion={updateQuestion}
              index={index}
              handleDeleteQuestion={handleDeleteQuestion}
              isLastQuestion={index === questions?.length - 1}
              documentInfo={documentInfo}
              updateMultipleQuestions={updateMultipleQuestions}
              resourceType={resourceType}
              rfiTagTypesOptions={rfiTagTypes}
              rfiTagSubtypesOptions={rfiTagSubtypes}
            />
          ))}
        </VStack>
        <HStack justifyContent={'space-between'} width={'100%'}>
          <Button onClick={handleAddNewQuestion} width='fit-content'>
            + Add new question
          </Button>
          <HStack spacing={4}>
            <Checkbox isChecked={sendRfiEmailToBinance} onChange={handleSendRfiEmailChange}>
              <Text fontWeight='bold'>
                Send RFI email to Binance
                {sendRfiEmailToBinance && emailType && (
                  <Text as='span' fontWeight='normal' color='gray.600'>
                    {' '}
                    ({EMAIL_TYPE_OPTIONS.find((opt) => opt.value === emailType)?.label})
                  </Text>
                )}
              </Text>
            </Checkbox>
            {kyc_status === 'active' && (
              <Checkbox isChecked={isKycStatusUpdate} onChange={() => setIsKycStatusUpdate(!isKycStatusUpdate)}>
                <Text fontWeight='bold'>Update {resourceType} Status to RFI Requested</Text>
              </Checkbox>
            )}
            <Button
              onClick={handleSubmit}
              width='fit-content'
              color='white'
              background='black'
              isLoading={loading}
              disabled={loading}
            >
              Submit
            </Button>
          </HStack>
        </HStack>
        <Modal isOpen={isEmailTypeModalOpen} onClose={handleEmailTypeCancel} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Select Email Type</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <RadioGroup value={pendingEmailType} onChange={setPendingEmailType}>
                <Stack spacing={3}>
                  {EMAIL_TYPE_OPTIONS.map((option) => (
                    <Radio key={option.value} value={option.value}>
                      {option.label}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant='ghost' mr={3} onClick={handleEmailTypeCancel}>
                Cancel
              </Button>
              <Button colorScheme='blue' onClick={handleEmailTypeConfirm} isDisabled={!pendingEmailType}>
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </LoadingErrorWrapper>
    </VStack>
  );
};

export default KycRfiForm;
